import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import * as yup from "yup";
import { postApi,getApi } from "../../ApiServices/api";
import { apiEndPoints } from "../../ApiServices/config";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { removeFromCart,updateCart } from "../../actions";
import { Country, State, City } from "country-state-city";
import { postcodeValidator } from "postcode-validator";

const usePayment = () => {
  const dispatch = useDispatch();
  const [country, setCountry] = useState([]);
  const [state, setState] = useState([]);
  const [city,setCity] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [paymentGateway, setPaymentGateway] = useState([]);
  const [paymentResponse, setPaymentResponse] = useState({});

  const isDevoteeLoggedIn = localStorage.getItem("token");
  const devoteeData = JSON.parse(localStorage.getItem("devoteeData"));
  const sankalpData = JSON.parse(localStorage.getItem("sankalpData"));

  const { bookingId } = useParams();
  const navigate = useNavigate();

  const serviceCartArr = useSelector((state) =>
    !!bookingId ? state.rentArr : state.cartArr
  );

  const getCountryData = ()=>{
    const countries = Country.getAllCountries();
    setCountry(countries);
  }
  
  const handleCountryChange = (countryCode) => {
    const states = State.getStatesOfCountry(countryCode);
    setState(states);
    setCity([]);
  };
    
  const handleStateChange = (stateCode, countryCode) => {
    const cities = City.getCitiesOfState(countryCode, stateCode);
    setCity(cities);
  };

  const schema = yup.object().shape({
    email: yup.string().email().required("Please Enter your Email"),
    country: yup.string().required("Country is required"),
    state: yup.string().required("State is required"),
    first_name: yup.string().required("First Name is required"),
    last_name: yup.string().required("Last Name is required"),
    address: yup.string().required("Address is required"),
    city: yup.string().required("City is required"),
    zipCode: yup.string().required("Zip Code is required").test("is-valid-zip", "Invalid Zip Code", function (value) {
      const { country, city } = this.parent;

      if (!city) {
        return this.createError({ message: "Select a city first" });
      }

      const isValid = postcodeValidator(value, country);
      return isValid;
    }),
  });

  const increaseQuantity = (data) => {
    let cartItem;
  
    if (data?.type === 4) {
      cartItem = serviceCartArr.find(item => item?.pooja_id === data?.pooja_id);
    } else {
      cartItem = serviceCartArr.find(item => item?._id === data?._id);
    }
  
    if (!cartItem) return;
  
    const updatedItem = {
      ...cartItem,
      quantity: cartItem.quantity + 1
    };
  
    dispatch(updateCart(updatedItem));
  };
  
  const decreaseQuantity = (data) => {
    let cartItem;
  
    if (data?.type === 4) {
      cartItem = serviceCartArr.find(item => item?.pooja_id === data?.pooja_id);
    } else {
      cartItem = serviceCartArr.find(item => item?._id === data?._id);
    }
    if (!cartItem || cartItem.quantity <= 0) return;
  
    const updatedItem = {
      ...cartItem,
      quantity: cartItem.quantity - 1
    };
  
    dispatch(updateCart(updatedItem));
  };
  
  useEffect(() => {
    if (bookingId) {
      const total = serviceCartArr.reduce(
        (sum, item) => sum + item?.total,
        0
      );
      setTotalAmount(total);
    } else {
      const total = serviceCartArr.reduce(
        (sum, item) => sum + Number(item.amount) * (item.quantity || 1),
        0
      );
      setTotalAmount(total);
    }
  }, [serviceCartArr, bookingId]);

  const productData = serviceCartArr?.map((item) => {
    if (item?.type !== 3) {
      return {
        _id: item._id,
        type: item.type,
        type_id: item.type_id,
        name: item.title,
        quantity: item.quantity || 1,
        price: item.amount,
        date: item.selectedDate || new Date(),
        cut_off_hours: item.cut_off_hours,
        cut_off_service_time: item.cut_off_service_time,
        cut_off_type: item.cut_off_type,
        location: item.location,
        booking_config: item.booking_config,
        row_data: {},
        page: item.page,
      };
    } else {
      return {
        _id: item._id,
        type: item.type,
        type_id: item.type_id,
        name: item.title,
        quantity: item.quantity || 1,
        price: item.total,
        date: item.selectedDate || new Date(),
      };
    }
  });

  const handleChildrenRemove = (item) => {
      dispatch(removeFromCart(item._id));
      toast.success("Item removed from the Basket");
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    setPaymentGateway(data.paymentGateway);
    try {
      await schema.validate(data);

      const payload = {
        lineItems: productData,
        totalAmount,
        payoutDetails: data,
      };

      if (isDevoteeLoggedIn) {
        payload.userId = devoteeData?.user_id;
      } else if (!!sankalpData) {
        payload.sankalpamId = sankalpData?._id;
      } else {
        navigate("/checkout-details");
        return;
      }

      const response = await postApi(apiEndPoints.makePayment, payload);

      if (response?.data?.status === 1) {
        localStorage.setItem("paymentId", response?.data?.paymentData?._id);

        switch (data.paymentGateway) {
          case 1:
            window.location.href = response?.data?.session?.url;
            break;
          case 2:
            setPaymentResponse(response?.data?.paymentData);
            break;
          default:
            break;
        }
      } else {
        toast.error("Something went wrong");
        setIsLoading(false);
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    getCountryData();
  }, []);

  return {
    isDevoteeLoggedIn,
    devoteeData,
    schema,
    isLoading,
    totalAmount,
    paymentGateway,
    onSubmit,
    paymentResponse,
    serviceCartArr,
    country,
    state,
    handleChildrenRemove,
    increaseQuantity,
    decreaseQuantity,
    handleStateChange,
    handleCountryChange,
    city,
    setCity,
    state,
    setState,
    country,
    setCountry
  };
};

export default usePayment;
